import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Consumer-grade chat composer (fn-48.3, R5).
//
// Behaviors over the operator SessionComposer baseline:
//   - Enter sends; Shift+Enter inserts a newline; an IME composition
//     commit NEVER sends (`e.nativeEvent.isComposing || e.keyCode ===
//     229` — 229 is the legacy composition keyCode some browsers
//     report after compositionend).
//   - Textarea autosizes to content (height = scrollHeight; CSS
//     max-height caps it).
//   - Focus STAYS in the composer across a send: the textarea is not
//     disabled while a send is in flight (only the button is), so the
//     user can keep typing the next message.
//   - Friendly strings only — the disabled reason and error copy come
//     from the copy module via props.
//
// Uncontrolled textarea (package precedent) — a `hasContent` flag
// drives the send button without per-keystroke controlled plumbing.
import { useRef, useState } from 'react';
import { COMPOSER_LABEL, COMPOSER_PLACEHOLDER, SENDING_LABEL, SEND_LABEL } from '../copy/copy.js';
import { autosizeTextArea, isComposerSendKey } from './composer-behaviors.js';
export function ChatComposer(props) {
    const inputRef = useRef(null);
    const [hasContent, setHasContent] = useState(false);
    const busy = props.busy === true;
    const submit = () => {
        const el = inputRef.current;
        if (el === null)
            return;
        const content = el.value.trim();
        // `busy` blocks the send path (agent responding) but NOT typing.
        if (content.length === 0 || props.disabled || busy || props.sending)
            return;
        el.value = '';
        autosizeTextArea(el);
        setHasContent(false);
        props.onSend(content);
        // Focus retention: the textarea keeps focus naturally (it is never
        // disabled mid-send); re-focus defensively for click-to-send.
        el.focus();
    };
    const onKeyDown = (e) => {
        if (!isComposerSendKey(e))
            return;
        e.preventDefault();
        submit();
    };
    return (_jsxs("form", { className: "sbui-chat-composer", "data-testid": "sbui-chat-composer", onSubmit: (e) => {
            e.preventDefault();
            submit();
        }, children: [_jsx("label", { className: "sbui-visually-hidden", htmlFor: "sbui-chat-composer-input", children: COMPOSER_LABEL }), _jsxs("div", { className: "sbui-chat-composer-row", children: [_jsx("textarea", { id: "sbui-chat-composer-input", ref: inputRef, className: "sbui-chat-composer-input", rows: 1, disabled: props.disabled, placeholder: props.disabled ? '' : COMPOSER_PLACEHOLDER, onInput: (e) => {
                            autosizeTextArea(e.currentTarget);
                            setHasContent(e.currentTarget.value.trim().length > 0);
                        }, onKeyDown: onKeyDown }), _jsx("button", { type: "submit", className: "sbui-chat-composer-send", disabled: props.disabled || busy || props.sending || !hasContent, children: props.sending ? SENDING_LABEL : SEND_LABEL })] }), props.disabled && props.disabledReason !== null && (_jsx("small", { className: "sbui-chat-composer-reason", "data-testid": "sbui-chat-composer-reason", children: props.disabledReason })), !props.disabled && busy && (props.busyReason ?? null) !== null && (_jsx("small", { className: "sbui-chat-composer-reason", "data-testid": "sbui-chat-composer-busy", children: props.busyReason })), props.error !== null && (_jsx("p", { className: "sbui-chat-composer-error", role: "alert", children: props.error }))] }));
}
//# sourceMappingURL=chat-composer.js.map